<template>
  <div class="preview-dialog-wrapper">
    <!-- Background Ambient Light Orbs -->
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>

    <!-- Fixed Header for Dialog Mode -->
    <div
      class="preview-dialog-top-bar d-flex justify-space-between align-center px-6 py-4 no-print"
    >
      <v-chip color="primary" outlined>
        <v-avatar left color="transparent">
          <v-icon color="primary">mdi-check-circle</v-icon>
        </v-avatar>
        LO Rename Request Preview
      </v-chip>
      <v-btn
        @click="closePrompt"
        class="action-btn back-btn white--text"
        rounded
        elevation="3"
      >
        <v-icon left>mdi-close</v-icon>
        Close Preview
      </v-btn>
    </div>

    <!-- Overlay Loading Progress -->
    <v-overlay :value="LoadingFlag" absolute>
      <v-progress-circular
        indeterminate
        size="64"
        color="#0097D7"
      ></v-progress-circular>
    </v-overlay>

    <div class="dialog-scroll-content">
      <v-container class="py-6 px-4 max-width-container">
        <!-- Main Preview Card -->
        <v-card
          class="preview-page-card pa-6 bg-preview-light font-outfit"
          flat
          v-if="records && records.OrderId"
        >
          <!-- MASSIVE HERO HEADER -->
          <v-card
            class="hero-banner mb-6 position-relative overflow-hidden"
            flat
          >
            <div class="hero-glow-overlay"></div>

            <!-- Floating Decorative Background Icons -->
            <v-icon
              class="bg-deco-icon icon-1"
              color="rgba(255, 255, 255, 0.05)"
              >mdi-domain</v-icon
            >
            <v-icon
              class="bg-deco-icon icon-2"
              color="rgba(255, 255, 255, 0.05)"
              >mdi-credit-card-outline</v-icon
            >
            <v-icon
              class="bg-deco-icon icon-3"
              color="rgba(255, 255, 255, 0.05)"
              >mdi-shield-check-outline</v-icon
            >

            <!-- Ocean Wave Effect at bottom -->
            <div class="hero-waves">
              <svg
                class="waves-svg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 24 150 28"
                preserveAspectRatio="none"
              >
                <defs>
                  <path
                    id="wave-path"
                    d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
                  />
                </defs>
                <g class="parallax-waves">
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="0"
                    fill="rgba(255, 255, 255, 0.05)"
                  />
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="3"
                    fill="rgba(255, 255, 255, 0.08)"
                  />
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="5"
                    fill="rgba(255, 255, 255, 0.03)"
                  />
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="7"
                    fill="rgba(255, 255, 255, 0.12)"
                  />
                </g>
              </svg>
            </div>

            <v-row
              class="ma-0 align-center position-relative fill-height text-content"
            >
              <!-- Left side Info -->
              <v-col cols="12" md="8" class="pa-8 z-index-2">
                <div class="d-flex align-center">
                  <v-avatar
                    color="rgba(255, 255, 255, 0.15)"
                    size="56"
                    class="mr-4 glow-icon-box"
                  >
                    <v-icon color="white" large>mdi-receipt</v-icon>
                  </v-avatar>
                  <div>
                    <h1 class="hero-title white--text">LO Rename Details</h1>
                    <p class="hero-subtitle mb-0">
                      View complete request, payment status and approval flow.
                    </p>
                  </div>
                </div>
              </v-col>

              <!-- Right side Status Card -->
              <v-col
                cols="12"
                md="4"
                class="pa-8 text-md-right d-flex justify-md-end z-index-2"
              >
                <div
                  class="status-badge-card pa-4 rounded-xl d-inline-flex align-center"
                >
                  <div class="mr-3 text-left">
                    <span class="status-label">Request Status</span>
                    <span class="status-value font-weight-black d-block">{{
                      records.ApprovalStatusTxt
                    }}</span>
                  </div>
                  <v-avatar
                    :color="
                      records.ApprovalStatus == 2
                        ? 'rgba(76, 175, 80, 0.15)'
                        : records.ApprovalStatus == 3
                        ? 'rgba(244, 67, 54, 0.15)'
                        : 'rgba(255, 152, 0, 0.15)'
                    "
                    size="48"
                    :class="
                      records.ApprovalStatus == 2
                        ? 'pulse-avatar-green'
                        : 'pulse-avatar-orange'
                    "
                  >
                    <v-icon
                      :color="
                        records.ApprovalStatus == 2
                          ? '#4caf50'
                          : records.ApprovalStatus == 3
                          ? '#f44336'
                          : '#ff9800'
                      "
                    >
                      {{
                        records.ApprovalStatus == 2
                          ? "mdi-check"
                          : records.ApprovalStatus == 3
                          ? "mdi-close"
                          : "mdi-clock-outline"
                      }}
                    </v-icon>
                  </v-avatar>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- ORDER SUMMARY STRIP -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Order ID</span>
                    <h3 class="strip-val font-mono">
                      {{ records.RzrpayOrderId }}
                    </h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-tag-outline</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Submitted Date</span>
                    <h3 class="strip-val">{{ records.Date }}</h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-calendar-month-outline</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Zone Name</span>
                    <h3 class="strip-val font-weight-black">
                      {{ records.ZoneName }}
                    </h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-map-marker-outline</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Total Amount</span>
                    <h3
                      class="strip-val price-val text-gradient-blue font-weight-black"
                    >
                      ₹{{
                        parseFloat(records.TotalAmount || 0).toLocaleString(
                          "en-IN"
                        )
                      }}
                    </h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-currency-inr</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- TWO COLUMNS GRID: SECTION 1 & 2 -->
          <v-row class="mb-6">
            <!-- RENAME DETAILS -->
            <v-col cols="12" md="6" class="py-2">
              <v-card
                class="section-card glass-card pa-6 rounded-2xl fill-height"
              >
                <div class="d-flex align-center mb-6">
                  <v-avatar
                    color="rgba(21, 101, 192, 0.1)"
                    size="48"
                    class="mr-3"
                  >
                    <v-icon color="#1565C0">mdi-account-outline</v-icon>
                  </v-avatar>
                  <h2 class="section-title">LO Information</h2>
                </div>

                <div class="d-flex align-center flex-wrap mb-6">
                  <v-avatar
                    size="64"
                    color="#1565C0"
                    class="member-large-avatar mr-4"
                  >
                    <span class="white--text font-weight-bold headline">{{
                      records.LomName
                        ? records.LomName.charAt(0).toUpperCase()
                        : "L"
                    }}</span>
                  </v-avatar>
                  <div>
                    <h3 class="member-title-name">
                      {{ records.LomName }}
                    </h3>
                    <span class="member-subtitle-id"
                      >Email: {{ records.EmailId }}</span
                    >
                  </div>
                </div>

                <v-divider class="mb-6 opacity-08"></v-divider>

                <v-row>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">LO Current Name</span>
                    <span class="item-val">{{ records.LomName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">Proposed Name</span>
                    <v-text-field
                      v-if="
                        showAction &&
                        RoleId == 2 &&
                        (records.ApprovalStatus == 1 ||
                          records.ApprovalStatus == 2 ||
                          records.ApprovalStatus == 3)
                      "
                      v-model="records.NewLoName"
                      outlined
                      dense
                      hide-details
                      class="mt-1 font-weight-bold"
                      background-color="white"
                    ></v-text-field>
                    <span
                      v-else
                      class="item-val font-weight-bold text-gradient-blue"
                      >{{ records.NewLoName }}</span
                    >
                  </v-col>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">Phone Number</span>
                    <span class="item-val">{{ records.PhoneNo }}</span>
                  </v-col>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">GST Number</span>
                    <span class="item-val"
                      ><code>{{ records.GstNumber || "N/A" }}</code></span
                    >
                  </v-col>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">LO Status</span>
                    <v-chip
                      v-if="records.LoStatusTxt"
                      :color="records.LoStatusColor"
                      dark
                      small
                      class="font-weight-bold"
                    >
                      {{ records.LoStatusTxt }}
                    </v-chip>
                    <span v-else class="item-val">N/A</span>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <!-- PAYMENT INFORMATION (FINTECH CARD) -->
            <v-col cols="12" md="6" class="py-2">
              <v-card
                class="section-card glass-card pa-6 rounded-2xl fill-height fintech-card"
              >
                <div class="d-flex align-center mb-6">
                  <v-avatar
                    color="rgba(21, 101, 192, 0.1)"
                    size="48"
                    class="mr-3"
                  >
                    <v-icon color="#1565C0">mdi-shield-check-outline</v-icon>
                  </v-avatar>
                  <h2 class="section-title">Payment Information</h2>
                </div>

                <div class="razorpay-feel-box pa-4 rounded-xl mb-4">
                  <div class="d-flex align-center justify-space-between mb-4">
                    <span class="rp-label">Razorpay Order ID</span>
                    <code class="rp-tag">{{
                      records.RzrpayOrderId || "N/A"
                    }}</code>
                  </div>
                  <div class="d-flex align-center justify-space-between mb-4">
                    <span class="rp-label">Razorpay Payment ID</span>
                    <code class="rp-tag">{{
                      records.RzrpayPaymentId || "N/A"
                    }}</code>
                  </div>
                  <div class="d-flex align-center justify-space-between mb-4">
                    <span class="rp-label">Status</span>
                    <span
                      :class="
                        records.RzrpayPaymentId
                          ? 'rp-value-success'
                          : 'rp-value-pending'
                      "
                    >
                      {{ records.RzrpayPaymentId ? "SUCCESS" : "PENDING" }}
                    </span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="rp-label">Transaction Date</span>
                    <span class="rp-value">{{ records.Date }}</span>
                  </div>
                </div>

                <v-divider class="mb-4 opacity-08"></v-divider>

                <div class="d-flex justify-space-between py-1">
                  <span class="summary-lbl">Base Amount</span>
                  <span class="summary-val"
                    >₹{{
                      parseFloat(records.Amount || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })
                    }}</span
                  >
                </div>
                <template v-if="records.InterState">
                  <div class="d-flex justify-space-between py-1">
                    <span class="summary-lbl">IGST (18%)</span>
                    <span class="summary-val"
                      >₹{{
                        parseFloat(records.IgstAmount || 0).toLocaleString(
                          "en-IN",
                          { minimumFractionDigits: 2 }
                        )
                      }}</span
                    >
                  </div>
                </template>
                <template v-else>
                  <div class="d-flex justify-space-between py-1">
                    <span class="summary-lbl">CGST (9%)</span>
                    <span class="summary-val"
                      >₹{{
                        parseFloat(records.CgstAmount || 0).toLocaleString(
                          "en-IN",
                          { minimumFractionDigits: 2 }
                        )
                      }}</span
                    >
                  </div>
                  <div class="d-flex justify-space-between py-1">
                    <span class="summary-lbl">SGST (9%)</span>
                    <span class="summary-val"
                      >₹{{
                        parseFloat(records.SgstAmount || 0).toLocaleString(
                          "en-IN",
                          { minimumFractionDigits: 2 }
                        )
                      }}</span
                    >
                  </div>
                </template>
                <v-divider class="my-2"></v-divider>
                <div class="d-flex justify-space-between py-1 align-center">
                  <span class="summary-lbl font-weight-bold headline"
                    >Grand Total</span
                  >
                  <span
                    class="summary-val grand-price text-gradient-blue font-weight-black"
                  >
                    ₹{{
                      parseFloat(records.TotalAmount || 0).toLocaleString(
                        "en-IN",
                        { minimumFractionDigits: 2 }
                      )
                    }}
                  </span>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- CURRENT PRESIDENT DETAILS -->
          <v-card class="section-card glass-card pa-6 rounded-2xl mb-6">
            <div class="d-flex align-center mb-6">
              <v-avatar color="rgba(21, 101, 192, 0.1)" size="48" class="mr-3">
                <v-icon color="#1565C0">mdi-account-tie</v-icon>
              </v-avatar>
              <h2 class="section-title">Current President Details</h2>
            </div>

            <v-row align="center">
              <v-col cols="12" sm="3" class="text-center py-2">
                <v-avatar
                  size="80"
                  color="#1565C0"
                  class="member-large-avatar mb-2"
                >
                  <v-img
                    v-if="records.PresidentPhoto"
                    :src="records.PresidentPhoto"
                    alt="President Photo"
                  ></v-img>
                  <span v-else class="white--text font-weight-bold headline">{{
                    records.PresidentFullName
                      ? records.PresidentFullName.charAt(0).toUpperCase()
                      : "P"
                  }}</span>
                </v-avatar>
                <h3 class="member-title-name">
                  {{ records.PresidentFullName || "N/A" }}
                </h3>
              </v-col>

              <v-col cols="12" sm="9" class="py-2">
                <v-row>
                  <v-col cols="12" sm="6" class="py-2">
                    <span class="item-lbl">President Name</span>
                    <span class="item-val font-weight-bold text-gradient-blue">
                      {{ records.PresidentFullName || "N/A" }}
                    </span>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <span class="item-lbl">Membership ID</span>
                    <span class="item-val">
                      <code>{{ records.PresidentMembershipId || "N/A" }}</code>
                    </span>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <span class="item-lbl">Mobile Number</span>
                    <span class="item-val">{{
                      records.PresidentMobile || "N/A"
                    }}</span>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <span class="item-lbl">Email Address</span>
                    <span class="item-val">{{
                      records.PresidentEmail || "N/A"
                    }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card>

          <!-- REMARKS HISTORY -->
          <v-card
            class="section-card glass-card pa-6 rounded-2xl mb-6"
            v-if="
              records.DescriptionHistory &&
              records.DescriptionHistory.length > 0
            "
          >
            <div class="d-flex align-center mb-6">
              <v-avatar color="rgba(21, 101, 192, 0.1)" size="48" class="mr-3">
                <v-icon color="#1565C0">mdi-history</v-icon>
              </v-avatar>
              <h2 class="section-title">Remarks History</h2>
            </div>
            <v-card
              outlined
              class="pa-4 mb-3"
              v-for="(history, index) in records.DescriptionHistory"
              :key="'history-' + index"
              style="
                background-color: #f8f9fa;
                border-radius: 12px;
                border-left: 4px solid #1565c0;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
              "
            >
              <div
                class="text-body-1"
                style="color: #263238"
                v-html="history"
              ></div>
            </v-card>
          </v-card>

          <!-- TAKE APPROVAL ACTION -->
          <v-card
            class="section-card glass-card pa-6 rounded-2xl mb-6"
            v-if="
              showAction &&
              RoleId == 2 &&
              (records.ApprovalStatus == 1 ||
                records.ApprovalStatus == 2 ||
                records.ApprovalStatus == 3)
            "
          >
            <div class="d-flex align-center mb-6">
              <v-avatar color="rgba(21, 101, 192, 0.1)" size="48" class="mr-3">
                <v-icon color="#1565C0"
                  >mdi-comment-text-multiple-outline</v-icon
                >
              </v-avatar>
              <h2 class="section-title">Take Approval Action</h2>
            </div>
            <v-form ref="approvalForm" v-model="validForm">
              <label
                class="font-weight-bold mb-2 d-block"
                style="color: #1565c0"
              >
                Remarks: <span style="color: red">*</span>
              </label>
              <v-textarea
                outlined
                v-model="Description"
                :rules="DescriptionRules"
                placeholder="Enter remarks or reason for decision..."
                dense
                rows="3"
                class="mt-2"
              ></v-textarea>

              <div class="d-flex justify-center mt-4" style="gap: 15px">
                <v-btn
                  @click.prevent="submitForm(2)"
                  :loading="approvalLoading"
                  :disabled="!validForm"
                  color="success"
                  large
                  class="white--text font-weight-bold px-8"
                  style="border-radius: 20px"
                >
                  <v-icon left>mdi-check-circle-outline</v-icon> Approve
                </v-btn>
                <v-btn
                  @click.prevent="submitForm(3)"
                  :loading="rejectionLoading"
                  :disabled="!validForm"
                  color="warning"
                  large
                  class="white--text font-weight-bold px-8"
                  style="border-radius: 20px"
                >
                  <v-icon left>mdi-close-circle-outline</v-icon> Reject
                </v-btn>
                <v-btn
                  @click="closePrompt"
                  color="grey"
                  large
                  class="white--text font-weight-bold px-8"
                  style="border-radius: 20px"
                >
                  Cancel
                </v-btn>
              </div>
            </v-form>
          </v-card>
        </v-card>
      </v-container>
    </div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";

export default {
  name: "LoRenamePreview",
  mixins: [common],
  props: {
    pageDescription: {
      type: String,
      default: "LO Rename Request Preview",
    },
    previewRecordPrompt: {
      type: Boolean,
      required: true,
    },
    recordData: {
      type: Object,
      required: true,
    },
    showAction: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      LoadingFlag: false,
      records: {},
      Description: "",
      DescriptionRules: [(v) => !!v || "Remarks / Description is required"],
      validForm: false,
      approvalLoading: false,
      rejectionLoading: false,
      RoleId: "",
    };
  },
  methods: {
    closePrompt() {
      this.$emit("hidePreviewRecordPrompt");
    },
    getOrderDetails() {
      var OrderId = this.recordData.lo_rename_order_id;
      if (!OrderId) return;

      this.LoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      const thisIns = this;
      this.$http({
        url: server_url + "api/lom-rename/order/show",
        method: "POST",
        data: { UserInterface: 2, OrderId: OrderId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          if (response.data.flag == 1) {
            thisIns.records = response.data.records;
          } else {
            thisIns.sweetAlert("error", response.data.output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
        });
    },
    submitForm(Status) {
      var OrderId = this.recordData.lo_rename_order_id;
      if (!OrderId) return;

      if (!this.Description) {
        this.sweetAlert("error", "Remarks is required.", false);
        return;
      }

      if (Status > 0) {
        if (Status === 2) this.approvalLoading = true;
        else if (Status === 3) this.rejectionLoading = true;

        this.LoadingFlag = true;
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;

        const thisIns = this;
        this.$http({
          url: server_url + "api/lom-rename/order/update-approval-status",
          method: "POST",
          data: {
            UserInterface: Status, // 2 = Approve, 3 = Reject
            OrderId: OrderId,
            Description: this.Description,
            NewLoName: this.records.NewLoName,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.LoadingFlag = false;
            thisIns.approvalLoading = false;
            thisIns.rejectionLoading = false;
            if (response.data.flag == 1) {
              thisIns.sweetAlert("success", response.data.output, false);
              thisIns.$emit("hidePreviewRecordPrompt");
            } else {
              thisIns.sweetAlert("error", response.data.output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.approvalLoading = false;
            thisIns.rejectionLoading = false;
          });
      }
    },
  },
  beforeMount() {
    this.RoleId = this.$session.get("RoleId");
    this.getOrderDetails();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.preview-dialog-wrapper {
  background-color: #f6f8fc !important;
  border-radius: 24px !important;
  overflow: hidden;
  position: relative;
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif !important;
}

.preview-dialog-wrapper
  *:not(.rp-tag):not(.font-mono):not(code):not(v-icon):not(.v-icon) {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif !important;
}

.preview-dialog-top-bar {
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 10;
}

.dialog-scroll-content {
  max-height: 80vh;
  overflow-y: auto;
}

.bg-preview-light {
  background-color: #f6f8fc;
}

/* Background floating light ambient orbs */
.bg-orb {
  position: absolute;
  width: 350px;
  height: 350px;
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.45;
  z-index: 0;
  pointer-events: none;
}

.bg-orb-1 {
  background: radial-gradient(circle, #42a5f5 0%, #1565c0 100%);
  top: -10%;
  left: -10%;
  animation: orbMovement1 15s infinite alternate ease-in-out;
}

.bg-orb-2 {
  background: radial-gradient(circle, #80deea 0%, #0097a7 100%);
  bottom: -10%;
  right: -10%;
  animation: orbMovement2 18s infinite alternate ease-in-out;
}

@keyframes orbMovement1 {
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(50px, 50px) scale(1.15);
  }
}

@keyframes orbMovement2 {
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(-60px, -40px) scale(1.1);
  }
}

/* Hero card customization */
.hero-banner {
  background: linear-gradient(
    135deg,
    #1565c0 0%,
    #1a73e8 45%,
    #42a5f5 100%
  ) !important;
  border-radius: 24px !important;
  color: white;
  min-height: 200px;
  box-shadow: 0 16px 40px rgba(21, 101, 192, 0.25) !important;
  animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.hero-glow-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.05),
    rgba(0, 0, 0, 0.15)
  );
  z-index: 1;
}

.hero-glow-overlay::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 7s infinite ease-in-out;
}

.text-content {
  position: relative;
  z-index: 3;
}

.hero-title {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.8px;
}

.hero-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
}

/* Background floating icons */
.bg-deco-icon {
  position: absolute !important;
  z-index: 1;
}

.icon-1 {
  font-size: 90px !important;
  right: 15%;
  top: -10px;
  transform: rotate(15deg);
  animation: floatShape 10s infinite ease-in-out;
}

.icon-2 {
  font-size: 70px !important;
  left: 10%;
  bottom: 10px;
  transform: rotate(-10deg);
  animation: floatShape 12s infinite ease-in-out alternate;
}

.icon-3 {
  font-size: 80px !important;
  right: 40%;
  bottom: -15px;
  transform: rotate(25deg);
  animation: floatShape 14s infinite ease-in-out;
}

/* Parallax Ocean Waves */
.hero-waves {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  z-index: 2;
  pointer-events: none;
}

.waves-svg {
  width: 100%;
  height: 40px;
}

.parallax-waves > use {
  animation: waveFlow 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax-waves > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax-waves > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax-waves > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax-waves > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

/* Action Buttons */
.action-btn {
  font-weight: 700 !important;
  font-size: 13px !important;
  padding: 10px 24px !important;
  text-transform: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.back-btn {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
}

.action-btn:hover {
  transform: translateY(-2.5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
}

/* Status Card */
.status-badge-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.status-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.7);
}

.status-value {
  font-size: 20px;
  color: white;
}

/* Glassmorphism Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  box-shadow: 0 10px 30px rgba(21, 101, 192, 0.04) !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.glass-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 16px 36px rgba(21, 101, 192, 0.08) !important;
  border-color: rgba(21, 101, 192, 0.15) !important;
}

/* Strip cards */
.strip-card {
  border-left: 4px solid #1565c0 !important;
  animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.strip-label {
  font-size: 13px;
  color: #78909c;
  font-weight: 600;
  text-transform: uppercase;
  display: block;
}

.strip-val {
  font-size: 18px;
  font-weight: 700;
  color: #263238;
}

.price-val {
  font-size: 22px;
}

/* Section Cards */
.section-title {
  font-size: 20px;
  font-weight: 800;
  color: #1565c0;
}

.member-large-avatar {
  border: 3.5px solid rgba(21, 101, 192, 0.2);
  box-shadow: 0 4px 15px rgba(21, 101, 192, 0.15);
}

.member-title-name {
  font-size: 20px;
  font-weight: 700;
  color: #263238;
}

.member-subtitle-id {
  font-size: 14px;
  color: #78909c;
  font-weight: 500;
}

.item-lbl {
  font-size: 13px;
  color: #78909c;
  font-weight: 600;
  display: block;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.item-val {
  font-size: 15px;
  font-weight: 600;
  color: #37474f;
}

/* Fintech card & Razorpay styling */
.fintech-card {
  border-top: 5px solid #1565c0 !important;
}

.razorpay-feel-box {
  background: rgba(21, 101, 192, 0.03);
  border: 1.5px dashed rgba(21, 101, 192, 0.15);
}

.rp-label {
  font-size: 13px;
  color: #546e7a;
  font-weight: 600;
}

.rp-tag {
  font-family: "Courier New", Courier, monospace;
  font-weight: 700;
  color: #1565c0;
  background: rgba(21, 101, 192, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 14px;
}

.rp-value {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
}

.rp-value-success {
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
  background: rgba(76, 175, 80, 0.12);
  padding: 2px 8px;
  border-radius: 4px;
}

.rp-value-pending {
  font-size: 13px;
  font-weight: 700;
  color: #e65100;
  background: rgba(255, 152, 0, 0.12);
  padding: 2px 8px;
  border-radius: 4px;
}

/* Receipt Summary Card */
.summary-lbl {
  font-size: 14px;
  color: #546e7a;
  font-weight: 600;
}

.summary-val {
  font-size: 16px;
  font-weight: 700;
  color: #37474f;
}

.grand-price {
  font-size: 24px;
}

/* Gradient text helpers */
.text-gradient-blue {
  background: linear-gradient(135deg, #1565c0 0%, #42a5f5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Animations */
@keyframes shineSweep {
  0% {
    left: -120%;
  }
  15%,
  100% {
    left: 120%;
  }
}

@keyframes waveFlow {
  0% {
    transform: translate3d(-90px, 0, 0);
  }
  100% {
    transform: translate3d(85px, 0, 0);
  }
}

@keyframes floatShape {
  0%,
  100% {
    transform: translateY(0) rotate(15deg) scale(1);
  }
  50% {
    transform: translateY(-15px) rotate(20deg) scale(1.05);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseGreen {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(76, 175, 80, 0));
  }
  50% {
    transform: scale(1.1);
    filter: drop-shadow(0 0 8px rgba(76, 175, 80, 0.6));
  }
}

@keyframes pulseOrange {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(255, 152, 0, 0));
  }
  50% {
    transform: scale(1.1);
    filter: drop-shadow(0 0 8px rgba(255, 152, 0, 0.6));
  }
}

.pulse-avatar-green {
  animation: pulseGreen 2.5s infinite ease-in-out;
}

.pulse-avatar-orange {
  animation: pulseOrange 2.5s infinite ease-in-out;
}

.z-index-2 {
  position: relative;
  z-index: 2;
}
</style>
